import { useAuthStore } from '@/state/store/auth';
import { getRefreshToken } from '@/modules/auth/services/auth.service';
import { isLocalhost } from './utils/localhost-checker/locahost-checker';

/**
 * HTTP Client Module
 *
 * A robust HTTP client utility that provides standardized methods for making API requests
 * with built-in error handling, authentication token management, and automatic token refresh.
 *
 * Features:
 * - Typed request/response handling with generics
 * - Standardized methods for GET, POST, PUT, DELETE operations
 * - Automatic handling of authentication token expiration
 * - Consistent error handling with custom HttpError class
 * - URL normalization for relative and absolute paths
 * - Configurable request headers
 * - Environment-based configuration
 *
 * @example
 * // GET request
 * const users = await clients.get<User[]>('users');
 *
 * // POST request with body
 * const newUser = await clients.post<User>(
 *   'users',
 *   JSON.stringify({ name: 'John', email: 'john@example.com' })
 * );
 *
 * // PUT request with custom headers
 * const updatedUser = await clients.put<User>(
 *   `users/${userId}`,
 *   JSON.stringify({ name: 'John Updated' }),
 *   { 'X-Custom-Header': 'value' }
 * );
 *
 * // DELETE request
 * const deleteResult = await clients.delete<{ success: boolean }>(`users/${userId}`);
 *
 * // Handling errors
 * try {
 *   const data = await clients.get<Data>('some-endpoint');
 *   // Process data
 * } catch (error) {
 *   if (error instanceof HttpError) {
 *     console.error(`API Error ${error.status}: ${error.message}`);
 *   }
 * }
 *
 * @note Requires environment variables:
 * - VITE_PUBLIC_BLOCKS_API_URL: Base URL for API requests
 * - VITE_X_BLOCKS_KEY: API key for authentication
 *
 */

interface Https {
  get<T>(url: string, headers?: HeadersInit): Promise<T>;
  post<T>(url: string, body: BodyInit, headers?: HeadersInit): Promise<T>;
  /** POST that returns the raw response text, for non-JSON bodies such as SSE streams. */
  postText(url: string, body: BodyInit, headers?: HeadersInit): Promise<string>;
  put<T>(url: string, body: BodyInit, headers?: HeadersInit): Promise<T>;
  patch<T>(url: string, body: BodyInit, headers?: HeadersInit): Promise<T>;
  delete<T>(url: string, headers?: HeadersInit, body?: BodyInit): Promise<T>;
  getBlob(url: string, headers?: HeadersInit, authRetried?: boolean): Promise<Blob>;
  getBlobDetail(
    url: string,
    headers?: HeadersInit,
    authRetried?: boolean
  ): Promise<{ blob: Blob; contentDisposition: string | null }>;
  request<T>(url: string, options: RequestOptions): Promise<T>;
  createHeaders(headers: any): Headers;
  handleAuthError<T>(
    url: string,
    method: string,
    headers: any,
    body: any,
    responseType?: ResponseType
  ): Promise<T>;
  handleAuthErrorBlob(url: string, headers: HeadersInit): Promise<Blob>;
}

type ResponseType = 'json' | 'text';

interface RequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: HeadersInit;
  body?: BodyInit;
  authRetried?: boolean;
  responseType?: ResponseType;
}

export class HttpError extends Error {
  status: number;
  error: Record<string, unknown>;

  constructor(status: number, error: Record<string, unknown>) {
    const errorMessage = typeof error.message === 'string' ? error.message : JSON.stringify(error);

    super(errorMessage);
    this.status = status;
    this.error = error;
    Object.setPrototypeOf(this, HttpError.prototype);
  }
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '');
const projectKey = import.meta.env.VITE_X_BLOCKS_KEY ?? '';
const localHostChecker = isLocalhost();

// IAM v4 picks the session cookie by the caller's host, read from Origin or Referer. A same-origin
// GET carries no Origin, so it needs a Referer; 'strict-origin' sends the origin only, never the
// page path (which can hold activation/reset codes).
const REFERRER_POLICY: ReferrerPolicy = 'strict-origin';

// Shared in-flight refresh call so concurrent 401s (e.g. multiple app-load
// requests) trigger a single token refresh instead of one per request.
let refreshPromise: ReturnType<typeof getRefreshToken> | null = null;

function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = getRefreshToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Only a rejection from the refresh endpoint itself (refresh token rejected as
// invalid/expired) should force a logout. Network failures / 5xx from the
// refresh call are transient - surface the error but keep the session intact
// so the next request can try the whole revalidate flow again.
function isInvalidRefreshTokenError(error: unknown): boolean {
  return error instanceof HttpError && (error.status === 400 || error.status === 401);
}

export const clients: Https = {
  async get<T>(url: string, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, { method: 'GET', headers });
  },

  async post<T>(url: string, body: BodyInit, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, { method: 'POST', headers, body });
  },

  async postText(url: string, body: BodyInit, headers: HeadersInit = {}): Promise<string> {
    const text = await this.request<string>(url, {
      method: 'POST',
      headers,
      body,
      responseType: 'text',
    });
    return text ?? '';
  },

  async put<T>(url: string, body: BodyInit, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, { method: 'PUT', headers, body });
  },

  async patch<T>(url: string, body: BodyInit, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, { method: 'PATCH', headers, body });
  },

  async delete<T>(url: string, headers: HeadersInit = {}, body?: BodyInit): Promise<T> {
    return this.request<T>(url, { method: 'DELETE', headers, body });
  },

  async getBlob(url: string, headers: HeadersInit = {}, authRetried = false): Promise<Blob> {
    const detail = await this.getBlobDetail(url, headers, authRetried);
    return detail.blob;
  },

  async getBlobDetail(
    url: string,
    headers: HeadersInit = {},
    authRetried = false
  ): Promise<{ blob: Blob; contentDisposition: string | null }> {
    const fullUrl = url.startsWith('http') ? url : `${BASE_URL}/${url.replace(/^\//, '')}`;
    const requestHeaders = this.createHeaders(headers);
    // GET has no body; JSON content-type is harmless but Accept helps
    requestHeaders.set('Accept', '*/*');

    const config: RequestInit = {
      method: 'GET',
      headers: requestHeaders,
      referrerPolicy: REFERRER_POLICY,
    };
    if (!localHostChecker) {
      config.credentials = 'include';
    }

    const response = await fetch(fullUrl, config);
    if (response.ok) {
      return {
        blob: await response.blob(),
        contentDisposition: response.headers.get('Content-Disposition'),
      };
    }
    if (response.status === 401) {
      // One refresh attempt per request. After a successful refresh, a second
      // 401 must throw — otherwise getBlob → handleAuthErrorBlob → getBlob loops.
      if (authRetried) {
        let err: Record<string, unknown>;
        try {
          err = await response.json();
        } catch {
          err = { error: response.statusText || 'Unauthorized' };
        }
        throw new HttpError(response.status, err);
      }
      const blob = await this.handleAuthErrorBlob(url, headers);
      return { blob, contentDisposition: null };
    }
    let err: Record<string, unknown>;
    try {
      err = await response.json();
    } catch {
      err = { error: response.statusText || 'Request failed' };
    }
    throw new HttpError(response.status, err);
  },

  async request<T>(
    url: string,
    { method, headers = {}, body, authRetried = false, responseType = 'json' }: RequestOptions
  ): Promise<T> {
    const fullUrl = url.startsWith('http') ? url : `${BASE_URL}/${url.replace(/^\//, '')}`;

    const requestHeaders = this.createHeaders(headers);
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      requestHeaders.delete('Content-Type');
    }

    const config: RequestInit = {
      method,
      headers: requestHeaders,
      referrerPolicy: REFERRER_POLICY,
    };

    if (!localHostChecker) {
      config.credentials = 'include';
    }

    if (body) {
      config.body = body;
    }

    try {
      const response = await fetch(fullUrl, config);

      if (response.ok) {
        const responseText = await response.text();
        if (!responseText.trim()) {
          return null as T;
        }

        return (responseType === 'text' ? responseText : JSON.parse(responseText)) as T;
      }

      if (response.status === 401) {
        // Parse error response first to check if it's a login error
        let err;
        try {
          err = await response.json();
        } catch {
          err = { error: response.statusText || 'Unauthorized' };
        }

        // If error has error_description, it's likely a login error, throw it directly.
        // One refresh attempt per request: a 401 after a successful refresh must throw,
        // otherwise request → handleAuthError → request loops (same guard as getBlobDetail).
        if (err.error_description || authRetried) {
          throw new HttpError(response.status, err);
        }

        // Otherwise, try to refresh token
        return this.handleAuthError<T>(url, method, headers, body, responseType);
      }

      let err;
      try {
        err = await response.json();
      } catch {
        err = { error: response.statusText || 'Request failed' };
      }
      throw new HttpError(response.status, err);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new HttpError(500, { error: 'Network error' });
    }
  },

  createHeaders(headers: any): Headers {
    // Attach Bearer when the store holds an access token. A cookie-only session
    // (IAM v4) has none, so Blocks calls must target VITE_API_BASE_URL, the host
    // the session cookie belongs to.
    const authToken = useAuthStore.getState().accessToken;

    const baseHeaders = {
      'Content-Type': 'application/json',
      'x-blocks-key': projectKey,
      ...(authToken && { Authorization: `Bearer ${authToken}` }),
    };

    const headerEntries =
      headers instanceof Headers ? Object.fromEntries(headers.entries()) : headers;

    const newHeader = new Headers({
      ...baseHeaders,
      ...headerEntries,
    });
    return newHeader;
  },

  async handleAuthError<T>(
    url: string,
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    headers: any,
    body: any,
    responseType: ResponseType = 'json'
  ): Promise<T> {
    const authStore = useAuthStore.getState();

    // Don't gate the refresh attempt on the store holding a refresh token:
    // in non-localhost environments the real refresh token lives in an
    // httpOnly cookie (requests go out with credentials: 'include') and the
    // store's `refreshToken` field can be empty even for a valid session.
    // Always try the refresh call and let its actual response decide.
    let refreshTokenRes;
    try {
      refreshTokenRes = await refreshAccessToken();
    } catch (error) {
      // Only force logout when the refresh token itself was rejected as
      // invalid/expired. A transient failure (network error, 5xx) shouldn't
      // wipe a valid session - let it surface as a failed request instead.
      if (isInvalidRefreshTokenError(error)) {
        authStore.logout();
      }
      throw error;
    }

    if (refreshTokenRes.refresh_token) {
      authStore.setTokens({
        accessToken: refreshTokenRes.access_token,
        refreshToken: refreshTokenRes.refresh_token,
      });
    } else {
      authStore.setAccessToken(refreshTokenRes.access_token);
    }

    return this.request<T>(url, { method, headers, body, authRetried: true, responseType });
  },

  async handleAuthErrorBlob(url: string, headers: HeadersInit = {}): Promise<Blob> {
    const authStore = useAuthStore.getState();

    // Don't gate the refresh attempt on the store holding a refresh token:
    // in non-localhost environments the real refresh token lives in an
    // httpOnly cookie (requests go out with credentials: 'include') and the
    // store's `refreshToken` field can be empty even for a valid session.
    // Always try the refresh call and let its actual response decide.
    let refreshTokenRes;
    try {
      refreshTokenRes = await refreshAccessToken();
    } catch (error) {
      // Only force logout when the refresh token itself was rejected as
      // invalid/expired. A transient failure (network error, 5xx) shouldn't
      // wipe a valid session - let it surface as a failed request instead.
      if (isInvalidRefreshTokenError(error)) {
        authStore.logout();
      }
      throw error;
    }

    if (refreshTokenRes.refresh_token) {
      authStore.setTokens({
        accessToken: refreshTokenRes.access_token,
        refreshToken: refreshTokenRes.refresh_token,
      });
    } else {
      authStore.setAccessToken(refreshTokenRes.access_token);
    }

    return this.getBlob(url, headers, true);
  },
};
